import React, { useState } from "react";
import { NavLink } from "react-router-dom";

import { Accordion, Avatar, Icon, PanelBase, Text } from "@performyard/py-components";

import { useNavigation } from "../NavigationContext";

interface MobileDrawerProps {
	isOpen: boolean;
	onClose: () => void;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({ isOpen, onClose }) => {
	// Get all navigation state from context
	const { config, mainNavItems, activeMainTab } = useNavigation();

	// Early return if no config
	if (!config) {
		return null;
	}

	const { currentUserId, currentUserName, currentUserJobTitle } = config;
	// Track which section is open (only one at a time, or null)
	const [openSection, setOpenSection] = useState<string | null>(null);

	const toggleSection = (sectionId: string) => {
		setOpenSection((prev) => (prev === sectionId ? null : sectionId));
	};

	return (
		<PanelBase isOpen={isOpen} onClose={onClose} side="right">
			<div className="flex h-full flex-col overflow-y-auto bg-white">
				{/* User Profile Section */}
				<div className="border-b border-neutral-3 bg-neutral-1 p-6">
					<div className="flex items-center gap-4">
						<Avatar size={48} id={currentUserId} />
						<div className="min-w-0 flex-1">
							<Text
								size="paragraph-2"
								dangerouslySetClassName="font-semibold text-neutral-9 mb-1 line-clamp-3"
							>
								{currentUserName}
							</Text>
							{currentUserJobTitle && (
								<Text size="paragraph-3" dangerouslySetClassName="text-neutral-7 line-clamp-2">
									{currentUserJobTitle}
								</Text>
							)}
						</div>
					</div>
				</div>

				{/* Main Navigation Sections */}
				<div className="flex-1 overflow-y-auto p-4">
					{mainNavItems.map((navItem) => {
						// Items with children render as accordions
						if (navItem.children && navItem.children.length > 0) {
							return (
								<Accordion
									key={navItem.id}
									isOpen={openSection === navItem.id}
									onToggle={() => toggleSection(navItem.id)}
									variant="underline"
									headerContent={
										<div className="flex items-center gap-3">
											<Icon
												dangerouslySetClassName="text-inherit"
												icon={navItem.icon}
												size={20}
												aria-hidden="true"
											/>
											<Text
												size="paragraph-2"
												dangerouslySetClassName="font-semibold text-neutral-8"
											>
												{navItem.title}
											</Text>
										</div>
									}
									className="mb-4"
								>
									<nav className="space-y-1 py-2">
										{navItem.children.map((child) => (
											<NavLink
												key={child.id}
												to={child.href}
												onClick={onClose}
												className={({ isActive }) =>
													`flex items-center gap-3 rounded-1 px-3 py-2 text-paragraph-2 transition-colors ${
														isActive
															? "bg-primary-1 font-medium text-primary-7"
															: "text-neutral-7 hover:bg-neutral-2 hover:text-neutral-9"
													}`
												}
											>
												{child.title}
											</NavLink>
										))}
									</nav>
								</Accordion>
							);
						}

						// Administration should use full page load
						if (navItem.id === "administration") {
							return (
								<a
									key={navItem.id}
									href={navItem.href}
									onClick={onClose}
									className={`mb-2 flex items-center gap-3 rounded-1 px-3 py-2 text-paragraph-2 transition-colors ${
										activeMainTab === navItem.id
											? "bg-primary-1 font-medium text-primary-7"
											: "text-neutral-7 hover:bg-neutral-2 hover:text-neutral-9"
									}`}
								>
									<Icon
										dangerouslySetClassName="text-inherit"
										icon={navItem.icon}
										size={20}
										aria-hidden="true"
									/>
									{navItem.title}
								</a>
							);
						}

						// Regular nav links without children
						return (
							<NavLink
								key={navItem.id}
								to={navItem.href}
								onClick={onClose}
								className={({ isActive }) =>
									`mb-2 flex items-center gap-3 rounded-1 px-3 py-2 text-paragraph-2 transition-colors ${
										isActive
											? "bg-primary-1 font-medium text-primary-7"
											: "text-neutral-7 hover:bg-neutral-2 hover:text-neutral-9"
									}`
								}
							>
								<Icon
									dangerouslySetClassName="text-inherit"
									icon={navItem.icon}
									size={20}
									aria-hidden="true"
								/>
								{navItem.title}
							</NavLink>
						);
					})}
				</div>

				{/* Get Help & Logout Section */}
				<div className="border-t border-neutral-3 p-6">
					<button
						type="button"
						onClick={() => {
							onClose();
							if (window.Beacon) {
								window.Beacon("open");
							}
						}}
						className="flex w-full items-center gap-3 rounded-1 px-3 py-2 text-left text-paragraph-2 text-neutral-7 transition-colors hover:bg-neutral-2 hover:text-neutral-9"
					>
						<i className="fa-solid fa-circle-question" aria-hidden="true" />
						Get Help
					</button>
					<a
						href="/logout/"
						className="flex items-center gap-3 rounded-1 px-3 py-2 text-paragraph-2 text-neutral-7 transition-colors hover:bg-neutral-2 hover:text-neutral-9"
					>
						<i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
						Logout
					</a>
				</div>
			</div>
		</PanelBase>
	);
};

export default MobileDrawer;
